/* eslint-disable react/forbid-foreign-prop-types */
import React, { ReactNode, FC, useMemo, ComponentType } from 'react';
import { ColProps as GridSystemColProps, Offsets, Push, Pull } from 'react-grid-system';
import { Breakpoint } from 'ia-react-core/~/interfaces/Breakpoint';
import { StyledCol } from './Col.styles';
import { WithTransclusionProps } from '../../../hocs/transclusion/withTransclusion';
import { WithSpacingProps } from '../../../hocs/grid/withSpacing';

const ALIASED_PROPS = ['offset', 'pull', 'push'] as const;
type AliasedProps = (typeof ALIASED_PROPS)[number];

export interface ColProps extends Omit<GridSystemColProps, AliasedProps>, WithTransclusionProps, WithSpacingProps {
  /** @ignore Content of the column. */
  children?: ReactNode;
  /** Custom element type. */
  as?: keyof JSX.IntrinsicElements | ComponentType<any>;
  /** @ignore */
  offset?: number | Offsets;
  /** @ignore */
  push?: number | Push;
  /** @ignore */
  pull?: number | Pull;
  /** The width of the column for this breakpoint. Either a number between 0 and 12, or "content". The offset/pull/push value for specific breakpoints. If passed as number, sp.specifies for every breakpoint (alias for xs). Defines the order of the element within the row. */
  order?: number;
  /** Alias for "xs". */
  span?: number | string;
  /** @ignore */
  gutter?: unknown;
}
/**
 * @since 0.14.0
 * @see See the styleguide's [grid standard](/Styleguide/Grid)
 * @component
 */
const Col: FC<ColProps> = ({ span, gutter, ...props }) => {
  const { xs } = props;
  const aliasedProps = useMemo(() => {
    const currentAliasedProps: {
      [P in Breakpoint]?: number | string;
    } & {
      [P in AliasedProps]?: { xs: ColProps[P] }
    } = {};
    if (typeof span !== 'undefined' && typeof xs === 'undefined') {
      currentAliasedProps.xs = span;
    }

    ALIASED_PROPS.forEach((aliasedProp) => {
      if (typeof props[aliasedProp] === 'number') {
        currentAliasedProps[aliasedProp] = { xs: props[aliasedProp] };
      }
    });

    return currentAliasedProps;
  }, [props, span, xs]);

  return (
    <StyledCol {...props} {...aliasedProps} />
  );
};

export default Col;
